'use client'

import {
  IHIdentifier,
  PatientProfile,
  QuestionSelectionError,
  QuizAnswer,
  QuizAnswerMap,
  QuizMetadataResponse,
  QuizState,
} from '@/lib/apis/types'
import {
  type QuizScreen,
  WL_CONSISTENCY_LEDGER_KEY,
  ED_CONFIDENCE_LEDGER_KEY,
} from '@/features/quiz/quiz-screen'
import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { TreatmentScreenGraphOrganizer } from '@/features/quiz/ed/treatment-screen-graph-organizer'
import { Session } from 'next-auth'
import { PointerWithOffset } from '@/features/quiz/quiz-screen-graph-organizer'
import TreatmentQuizScreenRenderer from '@/features/quiz/ed/treatment-quiz-screen-renderer'
import LoginModal from '@/features/auth/LoginModal'
import { useQueryState } from 'nuqs'
import {
  AUTHENTICATION_KEY,
  SCREEN_OFFSET_PARAM,
  SCREEN_STEP_PARAM,
} from '@/features/quiz/constants'
import { ED_AD_COPY_KEY, ED_FACTS_1_KEY } from '@/features/quiz/ed/constants'
import { useMutation } from '@tanstack/react-query'
import {
  TreatmentQuizAttribution,
  TreatmentType,
  UpdateQuizState,
} from '@/lib/apis/treatments'
import useApiClient from '@/lib/apis/clients/useApiClient'
import QuizErrorRenderer from '@/features/quiz/quiz-error-renderer'
import { useRouter } from 'next/navigation'
import { QuizErrorLinksByTreatment } from '@/lib/constants/links'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons'
import {
  isTreatmentQuizId,
  readTreatmentQuizProgress,
  removeTreatmentQuizProgress,
  type StoredTreatmentQuizProgress,
  TREATMENT_QUIZ_ID_PARAM,
  writeTreatmentQuizProgress,
} from '@/features/quiz/treatment-quiz-storage'
import GtmEventSender from '@/shared/gtm/GtmEventSender.tsx'
import TreatmentQuizMidEventSender from '@/features/quiz/treatment-quiz-mid-event-sender'
import { usePricingAmountCents } from '@/lib/cms/pricing'
import {
  LoyaltyRewardBanner,
  useWebLoyaltyReward,
} from '@/features/rewards/web-loyalty-reward'
import {
  captureTreatmentQuizAttribution,
  clearPendingTreatmentQuizAttribution,
  withMetaBrowserIds,
} from '@/features/quiz/treatment-quiz-attribution'

type Props = {
  session: Session | null
  metadata: QuizMetadataResponse
  initialState?: QuizState
  initialStoryId?: string | null
  profile: PatientProfile | null
  identifier: IHIdentifier | null
  type: TreatmentType
}

const ScreensWithoutSignInButton = new Set([
  ED_AD_COPY_KEY,
  ED_FACTS_1_KEY,
  AUTHENTICATION_KEY,
])

const ScreensWithoutBackButton = new Set([ED_AD_COPY_KEY, ED_FACTS_1_KEY])

function verifiedMobileFromState(quizState?: QuizState | null): string | null {
  if (!quizState) return null

  const billingDetails = quizState.answers?.['BillingDetails']
  if (
    !billingDetails ||
    billingDetails.type !== 'billing-details' ||
    !billingDetails.mobileVerified
  )
    return null

  return billingDetails.mobile
}

const QUIZ_EVENT_NAMES_BY_TREATMENT = {
  wl: { start: 'quiz_start_wq' },
  ed: { start: 'quiz_start_eqs' },
  pe: { start: 'quiz_start_pes' },
  mhl: { start: 'quiz_start_mhs' },
} as const satisfies Record<TreatmentType, { start: string }>

function findAuthenticationScreenPosition(
  screenGroups: Array<Array<QuizScreen>>,
) {
  for (const [groupIdx, group] of screenGroups.entries()) {
    for (const [screenIdx, screen] of group.entries()) {
      if (screen.type === 'generic' && screen.id === AUTHENTICATION_KEY) {
        return { groupIdx, screenIdx }
      }
    }
  }

  return null
}

export default function TreatmentQuizContainer({
  session,
  metadata,
  initialState,
  initialStoryId,
  type,

  ...props
}: Props) {
  // I don't want to refresh the router when the user logs in, because I am not
  // really positive it will not be bad. This needs to be tested thoroughly if
  // I were to refresh the router when the user logs in.
  const [loggedIn, setLoggedIn] = useState(!!session)
  const [savedStoryId, setSavedStoryId] = useState(initialStoryId ?? null)
  const [syncingQuiz, setSyncingQuiz] = useState(false)

  const [profile, setProfile] = useState<PatientProfile | null>(props.profile)
  const [identifier, setIdentifier] = useState<IHIdentifier | null>(
    props.identifier,
  )

  // A little bit wasted because offset is not used at all
  const [_, setOffset] = useQueryState(SCREEN_OFFSET_PARAM, {
    history: 'push',
    defaultValue: '0',
    clearOnDefault: true,
  })
  // Owned by the current screen (e.g. the IHI step inside the Medicare screen).
  const [screenStep, setScreenStep] = useQueryState(SCREEN_STEP_PARAM, {
    history: 'push',
  })
  const [quizId, setQuizId] = useQueryState(TREATMENT_QUIZ_ID_PARAM, {
    history: 'replace',
  })
  const activeQuizId = useRef<string | null>(
    isTreatmentQuizId(quizId) ? quizId : null,
  )
  const activeAttribution = useRef<TreatmentQuizAttribution | null>(null)
  const initializationStarted = useRef(false)

  const organizer = useRef<TreatmentScreenGraphOrganizer>(
    new TreatmentScreenGraphOrganizer(metadata.screenGroups, type),
  )
  const authenticationScreenPosition = useRef(
    findAuthenticationScreenPosition(metadata.screenGroups),
  )

  const [answers, setAnswers] = useState<QuizAnswerMap>(
    initialState?.answers ?? {},
  )

  const [pointer, setPointer] = useState<PointerWithOffset>()
  const [direction, setDirection] = useState(1)
  const [currentScreen, setCurrentScreen] = useState<QuizScreen>()

  // The 2fa verified mobile number is stored here, which allows us to verify
  // the user's mobile phone only once.
  const [verifiedMobile, setVerifiedMobile] = useState<string>(
    verifiedMobileFromState(initialState) ?? '',
  )

  function updateVerifiedMobile(quizState?: QuizState | null) {
    const mobileToUpdate = verifiedMobileFromState(quizState)
    setVerifiedMobile(mobileToUpdate ?? '')
  }

  const [answerError, setAnswerError] = useState<QuestionSelectionError | null>(
    null,
  )

  const [showLoginDialog, setShowLoginDialog] = useState(false)

  const router = useRouter()
  const errorLinks = QuizErrorLinksByTreatment[type]

  const client = useApiClient()
  const quizEventNames = QUIZ_EVENT_NAMES_BY_TREATMENT[type]
  const baseAmountCents = usePricingAmountCents(
    type === 'wl'
      ? 'weightLossInitialAssessment'
      : 'treatmentInitialConsultation',
  )
  const { data: rewardResponse } = useWebLoyaltyReward({
    treatmentType: type,
    baseAmountCents,
    enabled: loggedIn,
  })

  // Run the mutations in serial so updates are ordered
  const updateQuizStateMutation = useMutation({
    ...UpdateQuizState.mutation(client, type),
    mutationFn: ({
      state,
      includePii = false,
      attribution,
    }: {
      state: QuizState
      includePii?: boolean
      attribution?: TreatmentQuizAttribution | null
    }) =>
      UpdateQuizState.api(
        client,
        type,
        state,
        includePii,
        withMetaBrowserIds(attribution, document.cookie),
      ),
    scope: {
      id: type,
    },
    onSuccess: (result) => {
      setSavedStoryId(result.storyId ?? null)
      clearPendingTreatmentQuizAttribution(
        localStorage,
        activeAttribution.current,
      )
    },
  })

  function clearPersistedQuizProgress() {
    if (!activeQuizId.current) return
    removeTreatmentQuizProgress(localStorage, type, activeQuizId.current)
  }

  async function syncPersistedQuizProgress(
    progress: StoredTreatmentQuizProgress,
  ) {
    setSyncingQuiz(true)
    try {
      const result = await updateQuizStateMutation.mutateAsync({
        state: progress.state,
        includePii: true,
        attribution: progress.attribution ?? activeAttribution.current,
      })

      if (result.pii) {
        setProfile(result.pii.profile ?? null)
        setIdentifier((result.pii.identifier as IHIdentifier | null) ?? null)
      }

      switch (result.status) {
        case 'NotStarted':
        case 'InProgress':
        case 'Refreshed': {
          updateVerifiedMobile(result.state)

          if (
            result.state &&
            Object.keys(result.state.answers).length >
              Object.keys(progress.state.answers).length
          ) {
            const newPointer = organizer.current.initialize(true, result.state)
            setPointer(newPointer)
            setDirection(1)
            setCurrentScreen(newPointer.screen)
            setAnswers(result.state.answers)
            await setOffset(`${newPointer.offset}`, { history: 'replace' })
          }

          clearPersistedQuizProgress()
          return
        }
        case 'Completed':
          clearPersistedQuizProgress()
          router.push(errorLinks.alreadyCompletedQuiz)
          return
        case 'Rejected':
          clearPersistedQuizProgress()
          router.push(errorLinks.rejectedQuiz)
          return
      }
    } catch {
      // Keep the local copy so a failed sync cannot discard quiz progress.
      setSavedStoryId(null)
    } finally {
      setSyncingQuiz(false)
    }
  }

  // Anonymous quizzes own their state in local storage. A social login returns
  // to the same URL, so an authenticated mount can restore and sync that state
  // before continuing past the now-disabled authentication screen.
  useEffect(() => {
    if (initializationStarted.current) return
    initializationStarted.current = true

    let resolvedQuizId = isTreatmentQuizId(quizId) ? quizId : null
    if (!session && !resolvedQuizId) {
      resolvedQuizId = crypto.randomUUID()
      void setQuizId(resolvedQuizId)
    }
    activeQuizId.current = resolvedQuizId

    let persistedProgress = resolvedQuizId
      ? readTreatmentQuizProgress(localStorage, type, resolvedQuizId)
      : null
    const pendingAttribution = captureTreatmentQuizAttribution(
      localStorage,
      window.location.search,
    )
    activeAttribution.current = persistedProgress
      ? (persistedProgress.attribution ?? null)
      : pendingAttribution
    let state = persistedProgress?.state ?? initialState
    let result: PointerWithOffset

    try {
      result = organizer.current.restore(
        session !== null,
        state,
        persistedProgress?.position,
      )
    } catch {
      if (resolvedQuizId) {
        removeTreatmentQuizProgress(localStorage, type, resolvedQuizId)
      }
      persistedProgress = null
      state = initialState
      result = organizer.current.initialize(session !== null, initialState)
    }

    setAnswers(state?.answers ?? {})
    updateVerifiedMobile(state)
    setPointer(result)
    setDirection(1)
    setCurrentScreen(result.screen)
    void setOffset(`${result.offset}`, { history: 'replace' })

    if (session && persistedProgress) {
      void syncPersistedQuizProgress(persistedProgress)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (loggedIn || !pointer || !activeQuizId.current) return

    writeTreatmentQuizProgress(localStorage, type, activeQuizId.current, {
      state: { answers },
      attribution: activeAttribution.current,
      position: {
        pointer: pointer.pointer,
        offset: pointer.offset,
      },
    })
  }, [answers, loggedIn, pointer, type])

  function setPointerToPrev(current: PointerWithOffset): PointerWithOffset {
    const ptr = organizer.current.prev(current)

    setDirection(-1)
    setPointer(ptr)
    setCurrentScreen(ptr.screen)
    return ptr
  }

  // We can show the loading sign here
  if (!pointer || !currentScreen) return null

  const screenId = pointer.pointer.screenId
  const authenticationPosition = authenticationScreenPosition.current
  const isBeyondAuthenticationScreen =
    authenticationPosition !== null &&
    (pointer.pointer.groupIdx > authenticationPosition.groupIdx ||
      (pointer.pointer.groupIdx === authenticationPosition.groupIdx &&
        pointer.pointer.screenIdx > authenticationPosition.screenIdx))

  const showBackButton =
    (pointer?.pointer.groupIdx > 0 || pointer?.pointer.screenIdx > 0) &&
    !answerError &&
    !ScreensWithoutBackButton.has(screenId)

  const showSignInButton =
    !loggedIn &&
    pointer?.pointer.screenId &&
    !ScreensWithoutSignInButton.has(pointer?.pointer.screenId) &&
    !answerError

  // Functions to progress the user further in the quiz or go back to the
  // previously answered quiz.
  // `interacted` indicates whether the user has interacted with the question,
  // whether the organizer needs to update the group
  // `loggedIn` indicates whether the user is logged in or not. We cannot simply
  // use the session because the login state is tracked using a boolean due to
  // not being willing to refresh the router.
  // `updatedAnswers` is self-explanatory. Not using the state here because the
  // state might not have been updated when the caller executes this.
  async function next(
    interacted: boolean,
    loggedIn: boolean,
    updatedAnswers?: QuizAnswerMap,
  ) {
    if (!pointer) return

    const answersToUse = updatedAnswers ?? answers
    const next = organizer.current.next(pointer, answersToUse, {
      authenticated: loggedIn,
      data: interacted,
    })

    setPointer(() => next)
    setDirection(1)
    setCurrentScreen(next.screen)
    if (screenStep !== null) await setScreenStep(null)
    await setOffset(`${next.offset}`)

    // The quiz screens will show the loading state while the form is
    // submitting. The form state is reverted to "not submitting" before the
    // screen-change animation is complete since there is 0.1s delay in the
    // animation. To compensate for this, we add a 300ms delay here to ensure
    // the loading state is shown instead of flashing the screen.
    await new Promise((resolve) => setTimeout(resolve, 300))
  }

  async function prev() {
    if (!pointer) return

    const prev = setPointerToPrev(pointer)
    if (screenStep !== null) await setScreenStep(null)
    await setOffset(`${prev.offset}`)
  }

  async function back() {
    // Leave the screen's own sub-step first, e.g. IHI back to Medicare.
    if (screenStep !== null) {
      setDirection(-1)
      await setScreenStep(null)
      return
    }
    await prev()
  }

  async function onLogin() {
    // We need to wait for the answer from the upstream to ensure correctness
    setLoggedIn(true)

    const result = await updateQuizStateMutation.mutateAsync({
      state: { answers },
      includePii: true,
      attribution: activeAttribution.current,
    })

    clearPersistedQuizProgress()

    setShowLoginDialog(false)

    if (result.pii) {
      setProfile(result.pii.profile ?? null)
      setIdentifier((result.pii.identifier as IHIdentifier | null) ?? null)
    }

    switch (result.status) {
      case 'NotStarted':
      case 'InProgress':
      case 'Refreshed':
        // Update the verified mobile with what we have in the result
        updateVerifiedMobile(result.state)

        // Fewer answers than what we currently have, so don't even bother checking
        // and use the in-memory state.
        if (
          !result.state ||
          Object.keys(result.state?.answers ?? {}).length <=
            Object.keys(answers).length
        ) {
          organizer.current.refresh(true, answers)
          // Signing in through the "Sign In" link keeps the patient on the
          // question they were answering. Only the authentication screen
          // moves on.
          if (pointer?.pointer.screenId === AUTHENTICATION_KEY) {
            await next(true, true)
          }
          return
        }

        // This happens when the user has already answered a few questions but
        // decided to start a new quiz and login again. If this happens,
        // we discard the answers they have already answered and resume the
        // previous abandoned state.
        const newPointer = organizer.current.initialize(true, result.state)

        setPointer(newPointer)
        setDirection(1)
        setCurrentScreen(newPointer.screen)
        setAnswers(result.state.answers)

        await setOffset(`${newPointer.offset}`)

        return
      case 'Completed':
        router.push(errorLinks.alreadyCompletedQuiz)
        return
      case 'Rejected':
        router.push(errorLinks.rejectedQuiz)
        return
    }
  }

  async function onUpdateAnswer(
    questionKey: string,
    updates: QuizAnswer,
    goNext: boolean,
  ) {
    await onBatchUpdateAnswer([[questionKey, updates]], goNext)
  }

  async function onBatchUpdateAnswer(
    updates: Array<[string, QuizAnswer]>,
    goNext: boolean,
  ) {
    const updatedAnswers = updates.reduce(
      (acc, cur) => ({
        ...acc,
        [cur[0]]: cur[1],
      }),
      { ...answers },
    )

    if (loggedIn) {
      const update = {
        state: { answers: updatedAnswers },
        attribution: activeAttribution.current,
      }
      if (
        goNext &&
        updates.some(([, answer]) => answer.type === 'billing-details')
      ) {
        // The contact form must stay open and allow retry if its final save fails.
        await updateQuizStateMutation.mutateAsync(update)
      } else {
        updateQuizStateMutation.mutate(update)
      }
    }

    setAnswers(updatedAnswers)

    if (goNext) {
      await next(true, loggedIn, updatedAnswers)
    }
  }

  return (
    <>
      <GtmEventSender event={quizEventNames.start} values={{}} />
      {loggedIn &&
        !syncingQuiz &&
        savedStoryId &&
        isBeyondAuthenticationScreen && (
          <TreatmentQuizMidEventSender
            key={`${type}:${savedStoryId}`}
            storyId={savedStoryId}
            type={type}
          />
        )}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className={`bg-white pb-40 ${screenId === WL_CONSISTENCY_LEDGER_KEY || screenId === ED_CONFIDENCE_LEDGER_KEY ? 'pt-16 sm:pt-20' : 'pt-20'}`}
      >
        <AnimatePresence mode="wait" initial={false}>
          {showBackButton && (
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{
                ease: 'easeOut',
              }}
              className="fixed left-6 top-5 flex items-center justify-start text-sm text-gray-700 transition-all hover:text-gray-600 sm:text-base"
              onClick={async () => await back()}
            >
              <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
              <span>Back</span>
            </motion.button>
          )}
        </AnimatePresence>
        {!answerError &&
          (screenId !== 'MedicareCard' || rewardResponse?.reward) &&
          !(
            type === 'ed' &&
            (screenId === ED_CONFIDENCE_LEDGER_KEY ||
              screenId === 'EdServiceSelector')
          ) &&
          !(
            type === 'wl' &&
            (screenId === 'WlServiceSelector' ||
              screenId === WL_CONSISTENCY_LEDGER_KEY)
          ) && (
            <div className="w-full px-6 pb-8 sm:px-8 lg:px-10">
              <div className="mx-auto max-w-3xl">
                <LoyaltyRewardBanner response={rewardResponse} />
              </div>
            </div>
          )}
        <AnimatePresence mode="wait" initial={false} custom={direction}>
          <motion.div
            key={
              answerError
                ? answerError.type === 'generic-error'
                  ? answerError.cls
                  : answerError.error
                : screenId
            }
            custom={direction}
            variants={{
              // Transforms make fixed Continue bars follow the screen's height.
              // Slide with positioning so those bars stay tied to the viewport.
              begin: (d) => ({
                opacity: 0,
                left: d === 1 ? 20 : -20,
              }),
              enter: {
                opacity: 1,
                left: 0,
              },
              end: (d) => ({
                opacity: 0,
              }),
            }}
            initial="begin"
            animate="enter"
            exit="end"
            transition={{
              ease: 'easeOut',
              // going back should not delay anything
              delay: 0.1,
            }}
            className="relative w-full space-y-8 px-6 sm:px-8 lg:px-10"
          >
            {answerError ? (
              <QuizErrorRenderer error={answerError} />
            ) : (
              <TreatmentQuizScreenRenderer
                treatmentType={type}
                screen={currentScreen}
                screenId={screenId}
                onChange={onUpdateAnswer}
                onBatchChange={onBatchUpdateAnswer}
                onNext={async () => {
                  await next(false, loggedIn)
                }}
                onPrev={async () => {
                  await prev()
                }}
                onLogin={() => onLogin()}
                answered={false}
                answers={answers}
                onError={(error) => setAnswerError(error)}
                verifiedMobile={verifiedMobile}
                setVerifiedMobile={setVerifiedMobile}
                profile={profile}
                identifier={identifier}
                rewardResponse={rewardResponse}
              />
            )}
            {showSignInButton && (
              <div className="mt-12 flex justify-center sm:mt-16">
                <p className="text-gray-600">
                  Already have an account?{' '}
                  <button
                    className="cursor-pointer border-blue-700 text-blue-700 hover:border-b-2"
                    onClick={() => {
                      setShowLoginDialog(true)
                    }}
                  >
                    Sign In
                  </button>
                </p>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </motion.div>
      <LoginModal
        open={showLoginDialog}
        onClose={() => {
          setShowLoginDialog(false)
        }}
        onAuthenticated={onLogin}
        socialLoginMode="same-tab"
      />
    </>
  )
}
